import { memo } from 'react';
import moment from 'moment';
import { useTourStopFormTabs } from '@/hooks/useTourStopFormTabs';
import { TourStopFormTabContent } from './TourStopFormTabContent';

const FORM_TAB_PREFIX = 'formTab:';
function formTabIdFor(schemaId: number): string {
  return `${FORM_TAB_PREFIX}${schemaId}`;
}

// ── Helpers ──────────────────────────────────────────────────────────

function formatDateTime(dateTime: any): string {
  if (!dateTime) return '';
  return moment(dateTime).format('YYYY-MM-DD HH:mm');
}

function isTimeSensitiveEnabled(timeSensitive: any): boolean {
  if (!timeSensitive) return true;
  return moment().isAfter(timeSensitive.startDateTime, 'minutes');
}

function getTimeSensitiveClass(timeSensitive: any): string {
  if (!timeSensitive) return '';
  if (moment().isAfter(timeSensitive.endDateTime, 'minutes')) return 'expired';
  if (moment().isBetween(timeSensitive.startDateTime, timeSensitive.endDateTime)) return 'active';
  return 'upcoming';
}

function getTimeSensitiveText(timeSensitive: any): string {
  if (!timeSensitive) return '';
  if (moment().isAfter(timeSensitive.endDateTime, 'minutes')) {
    return `Expired ${moment(timeSensitive.endDateTime).fromNow()}`;
  }
  if (moment().isBetween(timeSensitive.startDateTime, timeSensitive.endDateTime)) {
    return `Expires ${moment(timeSensitive.endDateTime).fromNow()}`;
  }
  return `Starts ${moment(timeSensitive.startDateTime).fromNow()}`;
}

// ── Component ────────────────────────────────────────────────────────

interface TourStopDetailProps {
  currentStop: any;
  stopTab: string;
  isMobile: boolean;
  showStopsList: boolean;
  isLoadingStop: boolean;
  isCompleted: boolean;
  isMissed: boolean;
  isClaimedByMe: boolean;
  areOfficerCommentsDisabled: boolean;
  areOfficerAttachmentsEnabled: boolean;
  hasUpdateCompletedGuardTourStopRole: boolean;
  hasClaimedTour: boolean;
  isClaimingTour: boolean;
  sortedTasks: any[];
  enabledExceptions: any[];
  showingExceptions: boolean;
  selectedException: any;
  showAddIssue: boolean;
  newIssue: any;
  onSetStopTab: (tab: string) => void;
  onSetShowStopsList: (show: boolean) => void;
  onToggleTask: (task: any) => void;
  onShowTaskExemptions: (task: any) => void;
  isTaskCompletionExemptionMode: boolean;
  onToggleChecklistItem: (item: any) => void;
  onSetSelectedException: (exception: any) => void;
  onSaveException: () => void;
  onCancelException: () => void;
  onAddIssue: () => void;
  onCancelAddIssue: () => void;
  onSaveIssue: () => void;
  onSetNewIssue: (updater: (prev: any) => any) => void;
  onUploadIssuePhoto: (event: React.ChangeEvent<HTMLInputElement>, photoIndex: number) => void;
  onUploadStopAttachment: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onShowAttachment: (attachment: any, completedStop?: any) => void;
  onRemoveAttachment: (id: number) => void;
  onGetResizedImage: (id: number, completedStop?: any) => string;
  onIsResizedImageAvailable: (id: number, completedStop?: any) => boolean;
  onIsImageAttachment: (attachment: any) => boolean;
  onShowGeoFence: () => void;
  onSetCurrentStop: (updater: (prev: any) => any) => void;
  onCompleteStop: () => void;
  onClaimCurrentTour: () => void;
  onUpdateCompletedComment: (completedStop: any) => void;
}

export const TourStopDetail = memo(function TourStopDetail({
  currentStop,
  stopTab,
  isMobile,
  showStopsList,
  isLoadingStop,
  isCompleted,
  isMissed,
  isClaimedByMe,
  areOfficerCommentsDisabled,
  areOfficerAttachmentsEnabled,
  hasUpdateCompletedGuardTourStopRole,
  hasClaimedTour,
  isClaimingTour,
  sortedTasks,
  enabledExceptions,
  showingExceptions,
  selectedException,
  showAddIssue,
  newIssue,
  onSetStopTab,
  onSetShowStopsList,
  onToggleTask,
  onShowTaskExemptions,
  isTaskCompletionExemptionMode,
  onToggleChecklistItem,
  onSetSelectedException,
  onSaveException,
  onCancelException,
  onAddIssue,
  onCancelAddIssue,
  onSaveIssue,
  onSetNewIssue,
  onUploadIssuePhoto,
  onUploadStopAttachment,
  onShowAttachment,
  onRemoveAttachment,
  onGetResizedImage,
  onIsResizedImageAvailable,
  onIsImageAttachment,
  onShowGeoFence,
  onSetCurrentStop,
  onCompleteStop,
  onClaimCurrentTour,
  onUpdateCompletedComment,
}: TourStopDetailProps) {
  // Form-tabs projection. Loaded directly here (instead of via a parent prop)
  // because the data is keyed on the underlying TourStop template id and the
  // parent screen already juggles a lot of state — keeping the fetch local makes
  // this feature easy to add/remove without touching GuardToursScreen.
  //
  // currentStop is a GuardTourStop (one scheduled run of a stop), so its `id`
  // identifies that run. Form tabs are keyed on the TourStop *template* id
  // (currentStop.tourStopId) — the underlying location/client a stop is for —
  // since form instances live on the client and apply across every scheduled
  // run of stops at that client.
  const { tabs: formTabs } = useTourStopFormTabs({ tourStopId: currentStop?.tourStopId ?? null });

  return (
    <>
      {/* Stop Detail Panel */}
      <div className={`stop-detail-panel${isMobile && showStopsList ? ' hidden' : ''}`}>
        {isLoadingStop ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <div>Loading stop details...</div>
          </div>
        ) : currentStop ? (
          <>
            {/* Mobile Back Button */}
            {isMobile && (
              <button
                className="btn-back"
                onClick={() => onSetShowStopsList(true)}
                style={{ marginBottom: 16 }}
              >
                <i className="fa fa-arrow-left"></i> Back to Stops
              </button>
            )}

            {/* Stop Header */}
            <div className="stop-detail-header">
              <h2 className="stop-detail-title">{currentStop.title}</h2>
              <p className="stop-detail-location">{currentStop.location}</p>
            </div>

            {/* Stop Tabs */}
            <div className="stop-tabs">
              {!isCompleted && !isMissed && (
                <button
                  className={`stop-tab${stopTab === 'tasks' ? ' active' : ''}`}
                  onClick={() => onSetStopTab('tasks')}
                >
                  <i className="fa fa-tasks"></i> Tasks
                </button>
              )}
              {currentStop.areIssuesEnabled && (
                <button
                  className={`stop-tab${stopTab === 'issues' ? ' active' : ''}`}
                  onClick={() => onSetStopTab('issues')}
                >
                  <i className="fa fa-exclamation-triangle"></i> Issues
                </button>
              )}
              {currentStop.tourStopChecklist &&
                currentStop.tourStopChecklist.length > 0 &&
                !isCompleted &&
                !isMissed && (
                  <button
                    className={`stop-tab${stopTab === 'checklist' ? ' active' : ''}`}
                    onClick={() => onSetStopTab('checklist')}
                  >
                    <i className="fa fa-list-check"></i> Checklist
                  </button>
                )}
              {!areOfficerCommentsDisabled && !isCompleted && !isMissed && (
                <button
                  className={`stop-tab${stopTab === 'comments' ? ' active' : ''}`}
                  onClick={() => onSetStopTab('comments')}
                >
                  <i className="fa fa-comment"></i> Comments
                </button>
              )}
              {areOfficerAttachmentsEnabled && !isCompleted && !isMissed && (
                <button
                  className={`stop-tab${stopTab === 'attachments' ? ' active' : ''}`}
                  onClick={() => onSetStopTab('attachments')}
                >
                  <i className="fa fa-paperclip"></i> Attachments
                </button>
              )}
              {formTabs.map((ft) => {
                const id = formTabIdFor(ft.schemaId);
                return (
                  <button
                    key={id}
                    className={`stop-tab${stopTab === id ? ' active' : ''}`}
                    onClick={() => onSetStopTab(id)}
                  >
                    <i className="fa fa-clipboard-list"></i> {ft.tabTitle}
                  </button>
                );
              })}
              {currentStop.geofence && (
                <button
                  className={`stop-tab${stopTab === 'geofence' ? ' active' : ''}`}
                  onClick={() => {
                    onSetStopTab('geofence');
                    onShowGeoFence();
                  }}
                >
                  <i className="fa fa-street-view"></i> GeoFence
                </button>
              )}
              {currentStop.completionList && currentStop.completionList.length > 0 && (
                <button
                  className={`stop-tab${stopTab === 'completed' ? ' active' : ''}`}
                  onClick={() => onSetStopTab('completed')}
                >
                  <i className="fa fa-check-circle"></i> Completed
                </button>
              )}
            </div>

            {/* Tasks Tab */}
            {stopTab === 'tasks' && (
              <div className="tab-content">
                {currentStop.instructions && (
                  <div className="instructions-section">
                    <p className="instructions-text">{currentStop.instructions}</p>
                  </div>
                )}

                {showingExceptions ? (
                  <div className="exception-panel">
                    <h4 className="exception-title">
                      {isTaskCompletionExemptionMode
                        ? 'Select a task completion exception'
                        : 'Please provide your exception reason'}
                    </h4>
                    <div className="exception-list">
                      {enabledExceptions.map((exception: any) => (
                        <label key={exception.revisionGroup} className={`exception-option${selectedException === exception ? ' selected' : ''}`}>
                          <input
                            type="radio"
                            className="exception-radio"
                            name="exception"
                            checked={selectedException === exception}
                            onChange={() => onSetSelectedException(exception)}
                          />
                          <div className="exception-label">
                            <span className="exception-name">{exception.name}</span>
                            <span className="exception-description">{exception.description}</span>
                          </div>
                        </label>
                      ))}
                    </div>
                    <div className="exception-actions">
                      <button
                        className="btn-primary"
                        disabled={!selectedException}
                        onClick={onSaveException}
                      >
                        Save Exception
                      </button>
                      <button className="btn-secondary" onClick={onCancelException}>
                        Back to Tasks
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="task-list">
                    {sortedTasks.map((task: any) => {
                      const hasTaskExemptions = task.taskCompletionExemptionIds && task.taskCompletionExemptionIds.length > 0;
                      const isDisabled = isCompleted || isMissed || !isClaimedByMe || !isTimeSensitiveEnabled(task.timeSensitive);
                      const isException = task.isChecked && task.tourExemption && task.isTaskCompletionExemption;
                      return (
                        <div key={task.tourStopTaskId || task.id} className="task-item">
                          <div
                            className={`task-checkbox${task.isChecked ? ' checked' : ''}${isException ? ' exception' : ''}${isDisabled ? ' disabled' : ''}`}
                            onClick={() => onToggleTask(task)}
                          >
                            {task.isChecked && (
                              <span>
                                <i className={`fa ${isException ? 'fa-shield-alt' : 'fa-check'}`}></i>
                              </span>
                            )}
                          </div>
                          <div className="task-content">
                            <h4 className="task-name">{task.name}</h4>
                            {task.description && (
                              <p className="task-description">{task.description}</p>
                            )}
                            {task.timeSensitive && (
                              <div
                                className={`task-time-badge ${getTimeSensitiveClass(task.timeSensitive)}`}
                              >
                                <i className="fa fa-clock"></i>
                                {getTimeSensitiveText(task.timeSensitive)}
                              </div>
                            )}
                            {task.tourExemption && task.isTaskCompletionExemption && (
                              <div className="task-time-badge task-exemption-applied">
                                <i className="fa fa-shield-alt"></i> Exception: {task.tourExemption.name}
                              </div>
                            )}
                            {task.tourExemption && !task.isTaskCompletionExemption && (
                              <div className="task-time-badge expired">
                                Exception: {task.tourExemption.name}
                              </div>
                            )}
                            {hasTaskExemptions && !task.isChecked && !isDisabled &&
                              !(task.timeSensitive && moment().isAfter(task.timeSensitive.endDateTime, 'minutes')) && (
                              <button
                                className="task-exemption-btn"
                                onClick={(e) => { e.stopPropagation(); onShowTaskExemptions(task); }}
                              >
                                <i className="fa fa-shield-alt"></i> Complete with Exception
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Issues Tab */}
            {stopTab === 'issues' && (
              <div className="tab-content">
                {showAddIssue ? (
                  <div className="add-issue-form">
                    <div className="form-group">
                      <label className="form-label">Title</label>
                      <input
                        type="text"
                        className="form-input"
                        value={newIssue.title}
                        onChange={(e) =>
                          onSetNewIssue((prev: any) => ({ ...prev, title: e.target.value }))
                        }
                        placeholder="Enter issue title"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Description</label>
                      <textarea
                        className="form-input form-textarea"
                        value={newIssue.description}
                        onChange={(e) =>
                          onSetNewIssue((prev: any) => ({ ...prev, description: e.target.value }))
                        }
                        placeholder="Describe the issue"
                      ></textarea>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Photos</label>
                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        <div>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => onUploadIssuePhoto(e, 0)}
                          />
                          {newIssue.resizedImageList[0] &&
                            newIssue.resizedImageList[0].signedURL && (
                              <img
                                src={newIssue.resizedImageList[0].signedURL}
                                style={{
                                  maxWidth: 100,
                                  maxHeight: 100,
                                  marginTop: 8,
                                  borderRadius: 4,
                                }}
                                alt="Issue photo 1"
                              />
                            )}
                        </div>
                        <div>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => onUploadIssuePhoto(e, 1)}
                          />
                          {newIssue.resizedImageList[1] &&
                            newIssue.resizedImageList[1].signedURL && (
                              <img
                                src={newIssue.resizedImageList[1].signedURL}
                                style={{
                                  maxWidth: 100,
                                  maxHeight: 100,
                                  marginTop: 8,
                                  borderRadius: 4,
                                }}
                                alt="Issue photo 2"
                              />
                            )}
                        </div>
                      </div>
                    </div>
                    <div className="form-actions">
                      <button className="btn-secondary" onClick={onCancelAddIssue}>
                        Cancel
                      </button>
                      <button className="btn-primary" onClick={onSaveIssue}>
                        Save Issue
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    {!isCompleted && !isMissed && isClaimedByMe && (
                      <button className="btn-primary" style={{ marginBottom: 16 }} onClick={onAddIssue}>
                        <i className="fa fa-plus"></i> Add Issue
                      </button>
                    )}
                    <div className="issues-list">
                      {(currentStop.tourStopIssues || []).map((issue: any) => (
                        <div key={issue.id || issue.title} className="issue-card">
                          <div className="issue-card-header">
                            <span className="issue-title">{issue.title}</span>
                            <label className="issue-resolved-toggle">
                              <input
                                type="checkbox"
                                checked={issue.isResolved || false}
                                disabled={isCompleted || isMissed || !isClaimedByMe}
                                onChange={() => {
                                  issue.isResolved = !issue.isResolved;
                                  onSetCurrentStop((prev: any) => ({ ...prev }));
                                }}
                              />
                              Resolved
                            </label>
                          </div>
                          <div className="issue-body">
                            {issue.resizedImageList && issue.resizedImageList.length > 0 && (
                              <div className="issue-images">
                                {issue.resizedImageList
                                  .filter((img: any) => img.signedURL)
                                  .map((img: any, idx: number) => (
                                    <img
                                      key={idx}
                                      src={img.signedURL}
                                      className="issue-image"
                                      onClick={() => onShowAttachment(img)}
                                      alt={`Issue image ${idx + 1}`}
                                    />
                                  ))}
                              </div>
                            )}
                            <p className="issue-description">{issue.description}</p>
                            <div className="issue-meta">
                              {issue.raisedAt && <span>{formatDateTime(issue.raisedAt)}</span>}
                              {issue.raisedByName && <span> - {issue.raisedByName}</span>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Checklist Tab */}
            {stopTab === 'checklist' && (
              <div className="tab-content">
                <div className="checklist-list">
                  {(currentStop.tourStopChecklist || []).map((item: any) => (
                    <div key={item.id} className="checklist-item">
                      {!isCompleted ? (
                        <div
                          className={`task-checkbox${item.isChecked || item.completedAt ? ' checked' : ''}`}
                          onClick={() => onToggleChecklistItem(item)}
                        >
                          {(item.isChecked || item.completedAt) && (
                            <span>
                              <i className="fa fa-check" style={{ color: '#fff' }}></i>
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="task-checkbox checked disabled">
                          <i className="fa fa-check" style={{ color: '#fff' }}></i>
                        </div>
                      )}
                      <div className="checklist-content">
                        <p className="checklist-instruction">{item.instructions}</p>
                        {item.type && <span className="checklist-type">{item.type}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Comments Tab */}
            {stopTab === 'comments' && (
              <div className="tab-content">
                <div className="comments-section">
                  <textarea
                    className="comments-textarea"
                    value={currentStop.officerComments || ''}
                    disabled={isCompleted || isMissed || !isClaimedByMe || areOfficerCommentsDisabled}
                    placeholder="Enter your comments here..."
                    maxLength={4000}
                    onChange={(e) => {
                      const val = e.target.value;
                      onSetCurrentStop((prev: any) => ({
                        ...prev,
                        officerComments: val,
                      }));
                    }}
                  />
                </div>
              </div>
            )}

            {/* Attachments Tab */}
            {stopTab === 'attachments' && (
              <div className="tab-content">
                {!isCompleted && !isMissed && isClaimedByMe && (
                  <div style={{ marginBottom: 16 }}>
                    <input
                      type="file"
                      multiple
                      onChange={(e) => onUploadStopAttachment(e)}
                    />
                  </div>
                )}
                {(!currentStop.attachmentList || currentStop.attachmentList.length === 0) &&
                !isCompleted &&
                !isMissed &&
                isClaimedByMe ? (
                  <div className="empty-state">
                    No attachments yet. Use the button above to add attachments.
                  </div>
                ) : !currentStop.attachmentList || currentStop.attachmentList.length === 0 ? (
                  <div className="empty-state">No attachments.</div>
                ) : (
                  <div className="attachments-grid">
                    {currentStop.attachmentList.map((attachment: any) => (
                      <div
                        key={attachment.id}
                        className="attachment-item"
                        onClick={() => onShowAttachment(attachment)}
                      >
                        {onIsImageAttachment(attachment) ? (
                          onIsResizedImageAvailable(attachment.id) ? (
                            <img
                              src={onGetResizedImage(attachment.id)}
                              className="attachment-image"
                              alt={attachment.filename}
                            />
                          ) : (
                            <div className="attachment-file">
                              <i className="fa fa-image"></i>
                              <span className="attachment-filename">{attachment.filename}</span>
                            </div>
                          )
                        ) : (
                          <div className="attachment-file">
                            <i className="fa fa-file"></i>
                            <span className="attachment-filename">{attachment.filename}</span>
                          </div>
                        )}
                        {!isCompleted && !isMissed && isClaimedByMe && (
                          <button
                            className="attachment-remove"
                            onClick={(e) => {
                              e.stopPropagation();
                              onRemoveAttachment(attachment.id);
                            }}
                          >
                            <i className="fa fa-times"></i>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Form-tab content. Rendered as siblings (one block per active form
                tab) so each schema's data lives under its own selectable tab,
                matching the rest of the tab strip. */}
            {formTabs.map((ft) => {
              const id = formTabIdFor(ft.schemaId);
              if (stopTab !== id) return null;
              return (
                <div key={id} className="tab-content">
                  <TourStopFormTabContent tab={ft} />
                </div>
              );
            })}

            {/* GeoFence Tab */}
            {stopTab === 'geofence' && (
              <div className="tab-content">
                <div id="map" className="geofence-map"></div>
              </div>
            )}

            {/* Completed Tab */}
            {stopTab === 'completed' && (
              <div className="tab-content">
                <div className="completed-list">
                  {(currentStop.completionList || []).map((completion: any) => (
                    <div key={completion.id} className="completed-item">
                      <div className="completed-meta">
                        <div>
                          <span className="completed-time">
                            {formatDateTime(completion.completedAt)}
                          </span>
                          <span className="completed-by">by {completion.completedByName}</span>
                        </div>
                      </div>
                      <textarea
                        className="comments-textarea"
                        value={completion.officerComments || ''}
                        disabled={!hasUpdateCompletedGuardTourStopRole}
                        maxLength={4000}
                        style={{ minHeight: 100, marginBottom: 8 }}
                        onChange={(e) => {
                          completion.officerComments = e.target.value;
                          onSetCurrentStop((prev: any) => ({ ...prev }));
                        }}
                      />
                      {hasUpdateCompletedGuardTourStopRole && (
                        <button
                          className="btn-primary"
                          onClick={() => onUpdateCompletedComment(completion)}
                        >
                          Update Comment
                        </button>
                      )}
                      {completion.attachmentList && completion.attachmentList.length > 0 && (
                        <div className="attachments-grid" style={{ marginTop: 12 }}>
                          {completion.attachmentList.map((attachment: any) => (
                            <div
                              key={attachment.id}
                              className="attachment-item"
                              onClick={() => onShowAttachment(attachment, completion)}
                            >
                              {onIsResizedImageAvailable(attachment.id, completion) ? (
                                <img
                                  src={onGetResizedImage(attachment.id, completion)}
                                  className="attachment-image"
                                  alt={attachment.filename}
                                />
                              ) : (
                                <div className="attachment-file">
                                  <i className="fa fa-file"></i>
                                  <span className="attachment-filename">
                                    {attachment.filename}
                                  </span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="empty-state">Select a stop from the list to view details.</div>
        )}
      </div>

      {/* Action Bar */}
      {currentStop && (
        <div className="action-bar">
          <div className="action-bar-status">
            {isCompleted && (
              <span className="status-badge completed">
                <i className="fa fa-check-circle"></i> Stop Completed
              </span>
            )}
            {!isCompleted && isMissed && (
              <span className="status-badge missed">
                <i className="fa fa-times-circle"></i> Stop Missed
              </span>
            )}
          </div>
          <div>
            {!hasClaimedTour ? (
              <button className="btn-claim" disabled={isClaimingTour} onClick={onClaimCurrentTour}>
                {isClaimingTour ? 'Claiming...' : 'Claim Tour'}
              </button>
            ) : (
              !isCompleted &&
              isClaimedByMe && (
                <button className="btn-complete" onClick={onCompleteStop}>
                  Complete Stop
                </button>
              )
            )}
          </div>
        </div>
      )}
    </>
  );
});
